// components/list/saveFactoryArchetype.ts
import type { FactoryArchetypeAbstractDirection } from '#lib/list/factoryArchetypeDirection';

type SavedArchetype = { id: string; name: string; handle: string; revision: number; sourceDescription?: string; promptOverride?: string; derivedFromReferences?: boolean };

/** Shared by the explicit chat command and the Director tool; API owns permissions and concurrency. */
export async function saveFactoryArchetype(input: {
  workspaceId: string; name?: string; description?: string; handle?: string; newHandle?: string; imageAssetPath?: string; referenceImages?: { imageAssetPath: string }[]; abstractDirection?: FactoryArchetypeAbstractDirection; promptOverride?: string; deriveFromReferences?: boolean;
}): Promise<SavedArchetype> {
  const current = await fetch(`/api/list/factory-archetypes?workspaceId=${encodeURIComponent(input.workspaceId)}`, { cache: 'no-store' });
  const profile = await current.json().catch(() => null) as {
    archetypes?: SavedArchetype[]; factoryProfileRevision?: number; canEdit?: boolean; message?: string;
  } | null;
  if (!current.ok || !profile?.factoryProfileRevision || !profile.canEdit) {
    throw new Error(profile?.message || 'Only a Westfield brand administrator can manage these archetypes.');
  }
  const handle = input.handle?.replace(/^@/, '').toLowerCase();
  const target = handle ? profile.archetypes?.find((archetype) => archetype.handle === handle) : undefined;
  if (handle && !target) throw new Error(`@${handle} is not an available Factory archetype.`);
  const response = await fetch('/api/list/factory-archetypes', {
    method: target ? 'PATCH' : 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      workspaceId: input.workspaceId,
      expectedRevision: profile.factoryProfileRevision,
      ...(target ? { archetypeId: target.id } : input.referenceImages !== undefined ? { referenceImages: input.referenceImages } : { imageAssetPath: input.imageAssetPath }),
      name: input.name ?? target?.name,
      description: input.description ?? (input.deriveFromReferences ? undefined : (target?.promptOverride?.trim() || target?.sourceDescription)),
      ...(input.abstractDirection !== undefined ? { abstractDirection: input.abstractDirection } : {}),
      ...(input.promptOverride !== undefined ? { promptOverride: input.promptOverride } : {}),
      ...(input.deriveFromReferences !== undefined ? { deriveFromReferences: input.deriveFromReferences } : {}),
      ...(input.newHandle !== undefined ? { handle: input.newHandle } : {}),
    }),
  });
  const body = await response.json().catch(() => null) as { archetype?: SavedArchetype; derivedFromReferences?: boolean; message?: string } | null;
  if (!response.ok || !body?.archetype?.handle) throw new Error(body?.message || 'Factory could not save this archetype.');
  window.dispatchEvent(new CustomEvent('factory-archetypes-changed', { detail: { workspaceId: input.workspaceId } }));
  return body.derivedFromReferences ? { ...body.archetype, derivedFromReferences: true } : body.archetype;
}
